// frontend/src/widgets/DynamicReportBuilder.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-07-01
// Updated:      2026-09-08 09:26 BST
// Description:  Report Admin / Builder UI.
//               - Loads templates
//               - Loads live available fields from backend for view-backed reports
//               - Supports selected-field ordering
//               - Admins can update/delete existing templates
//               - Non-admins can run from templates and save as new templates only
//               - is_standard is treated as admin-locked in the current schema
//               - Includes inline debug state to diagnose preview execution issues
//
// Mobile Responsiveness (2026-09-08):
//               - Preserves the existing desktop report-builder layout and API behaviour.
//               - Uses a single mobile field workspace with explicit Available Fields /
//                 Selected Order switching instead of stacking two large 420px panels.
//               - Keeps one mounted field workspace on mobile while desktop retains both.
//               - Makes template controls, dates, checkboxes, ordering controls, and
//                 Preview/Save actions touch-friendly below 768px.
//               - Keeps Debug Status available but collapsible on mobile to reduce clutter.
//               - Expands the nearest ReactGridLayout host to the complete rendered mobile
//                 height because mobile widgets do not expose the desktop resize handle.
//               - ReportDisplayModal remains the existing single preview-result path.
//
// Operational Notes:
//               - Reporting API routes, template permissions, field ordering semantics,
//                 date validation, preview payloads, save/update/delete behaviour, and
//                 inline diagnostics are intentionally unchanged.
//               - Existing hard-coded admin-role compatibility remains unchanged here;
//                 authorisation migration belongs to the separate RBAC workstream.

import React, { useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { format, parseISO } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";

import axiosInstance from "../api/axiosInstance";
import ReportDisplayModal from "../components/ReportDisplayModal";
import { useTheme } from "../context/ThemeContext.jsx";
import { useAlerts } from "../context/AlertContext";
import { useAuth } from "../context/AuthContext";
import { FaTrash } from "react-icons/fa";

const FRIENDLY_FIELD_NAMES = {
  job_id: "Job ID",
  client_name: "Client",
  status: "Status",
  created_at: "Created At",
  id: "ID",
  username: "Username",
  email: "Email",
  first_name: "First Name",
  last_name: "Last Name",
  role: "Role",
  last_login_at: "Last Login",
  last_active_at: "Last Active",
  timestamp: "Timestamp",
  run_id: "Run ID",
  frame_ts_utc: "Frame Time",
  plate_normalized: "Plate",
  display_plate: "Display Plate",
  yolo_conf: "Confidence",
  camera_name: "Camera",
  sighting_vehicle_make: "Sighting Make",
  sighting_vehicle_model: "Sighting Model",
  sighting_vehicle_color: "Sighting Colour",
  sighting_vehicle_category: "Sighting Category",
  vehicle_make: "Resolved Make",
  vehicle_model: "Resolved Model",
  vehicle_colour: "Resolved Colour",
  vehicle_fuel_type: "Fuel Type",
  vehicle_type_hint: "Type Hint",
  dvla_make: "DVLA Make",
  dvla_colour: "DVLA Colour",
  dvla_fuel_type: "DVLA Fuel",
  dvla_year_of_manufacture: "Year",
  dvla_tax_status: "Tax Status",
  dvla_tax_due_date: "Tax Due",
  dvla_mot_status: "MOT Status",
  dvla_mot_expiry_date: "MOT Expiry",
  dvla_engine_capacity_cc: "Engine CC",
  dvla_co2_emissions_gpkm: "CO2 g/km",
  dvla_revenue_weight_kg: "Revenue Weight KG",
  dvla_wheelplan: "Wheelplan",
  dvla_type_approval: "Type Approval",
  dvla_fetched_at: "DVLA Cached At",
  dvla_cached_at: "DVLA Applied At",
  sighting_count: "Sightings",
  first_seen_at: "First Seen",
  last_seen_at: "Last Seen",
  first_seen_s: "First Seen",
  last_seen_s: "Last Seen",
  dwell_s: "Dwell (s)",
  hit_count: "Hits",
  max_yolo_conf: "Max Confidence",
  avg_yolo_conf: "Avg Confidence",
  best_snapshot_url: "Snapshot Image",
  best_snapshot_path: "Best Snapshot Path",
  plate_image_url: "Plate Image",
  plate_image_path: "Plate Image Path",
  snapshot_url: "Snapshot Image URL",
  snapshot_path: "Snapshot Path",
  plate_secondary_url: "Plate Image URL",
  plate_secondary_path: "Plate Image Path",
  overview_url: "Overview Image URL",
  overview_path: "Overview Path",
  plate_b64_len: "Plate Bytes",
  overview_b64_len: "Overview Bytes",
  plate_secondary_b64_len: "Secondary Plate Bytes",
  coba_classification: "COBA Class",
  coba_confidence: "COBA Confidence",
  simplified_coba_classification: "Simple COBA",
  session_idx: "Session #",
  speed_mps_avg: "Avg Speed (m/s)",
  heading_deg_avg: "Avg Heading (deg)",
  lat_first: "Lat First",
  lon_first: "Lon First",
  lat_last: "Lat Last",
  lon_last: "Lon Last",
  camera_device_id: "Camera Device ID",
  dwell_seconds: "Dwell (s)",
  dwell_minutes: "Dwell (min)",
  max_confidence_pct: "Max Confidence",
  avg_confidence_pct: "Avg Confidence",
};

const safeArray = (v) => (Array.isArray(v) ? v : []);

const MOBILE_BREAKPOINT = 768;

const moveItem = (items, fromIndex, toIndex) => {
  if (
    !Array.isArray(items) ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= items.length ||
    toIndex >= items.length ||
    fromIndex === toIndex
  ) {
    return items;
  }

  const next = [...items];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

const DynamicReportBuilder = () => {
  const { theme } = useTheme();
  const { triggerAlert } = useAlerts();
  const { currentUser } = useAuth();

  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [availableFields, setAvailableFields] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);

  const [reportName, setReportName] = useState("");
  const [sourceView, setSourceView] = useState("");
  const [isViewBacked, setIsViewBacked] = useState(false);
  const [isStandard, setIsStandard] = useState(false);
  const [availableToClients, setAvailableToClients] = useState(false);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [reportResult, setReportResult] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [debugState, setDebugState] = useState({
    stage: "idle",
    message: "",
    lastPayload: null,
    lastResponseSummary: null,
  });

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < MOBILE_BREAKPOINT : false
  );
  const [mobileFieldPanel, setMobileFieldPanel] = useState("available");

  const rootRef = useRef(null);

  const roleName = String(
    currentUser?.role?.name ??
      currentUser?.role_name ??
      currentUser?.role ??
      ""
  )
    .trim()
    .toLowerCase();

  const roleIdNum = Number(
    currentUser?.role_id ??
      currentUser?.role?.id ??
      currentUser?.role ??
      NaN
  );

  const canAdminManageTemplates =
    roleName === "admin" ||
    roleName.includes("super admin") ||
    roleName.includes("system admin") ||
    [1, 2, 3].includes(roleIdNum);

  const canUpdateExistingTemplate = !!selectedTemplateId && canAdminManageTemplates;
  const canDeleteExistingTemplate = !!selectedTemplateId && canAdminManageTemplates;
  const mustSaveAsNew = !!selectedTemplateId && !canAdminManageTemplates;

  const selectedFieldsSet = useMemo(() => new Set(selectedFields), [selectedFields]);

  const unselectedFields = useMemo(
    () => availableFields.filter((f) => !selectedFieldsSet.has(f)),
    [availableFields, selectedFieldsSet]
  );

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    console.log("[DynamicReportBuilder] render state", {
      showModal,
      hasReportResult: !!reportResult,
      reportRows: Array.isArray(reportResult?.data) ? reportResult.data.length : null,
      debugState,
    });
  }, [showModal, reportResult, debugState]);

  useEffect(() => {
    const updateViewportMode = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    updateViewportMode();
    window.addEventListener("resize", updateViewportMode);

    return () => {
      window.removeEventListener("resize", updateViewportMode);
    };
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const gridHost = root.closest(".react-grid-item");
    if (!gridHost) return undefined;

    const previousHeight = gridHost.style.height;
    const previousMinHeight = gridHost.style.minHeight;

    let frameId = null;
    let observer = null;

    const restoreDesktopHeight = () => {
      gridHost.style.height = previousHeight;
      gridHost.style.minHeight = previousMinHeight;
    };

    const syncMobileHostHeight = () => {
      if (frameId) cancelAnimationFrame(frameId);

      frameId = requestAnimationFrame(() => {
        if (!isMobile) {
          restoreDesktopHeight();
          return;
        }

        const hostRect = gridHost.getBoundingClientRect();
        const rootRect = root.getBoundingClientRect();
        const topOffset = Math.max(0, rootRect.top - hostRect.top);
        const requiredHeight = Math.ceil(topOffset + root.scrollHeight + 8);

        gridHost.style.height = `${requiredHeight}px`;
        gridHost.style.minHeight = `${requiredHeight}px`;
      });
    };

    syncMobileHostHeight();
    window.addEventListener("resize", syncMobileHostHeight);

    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(syncMobileHostHeight);
      observer.observe(root);
    }

    return () => {
      window.removeEventListener("resize", syncMobileHostHeight);
      if (observer) observer.disconnect();
      if (frameId) cancelAnimationFrame(frameId);
      restoreDesktopHeight();
    };
  }, [
    isMobile,
    mobileFieldPanel,
    selectedTemplateId,
    selectedFields.length,
    availableFields.length,
    loadingTemplates,
    debugState.stage,
    debugState.message,
    reportName,
    startDate,
    endDate,
    availableToClients,
    isStandard,
  ]);

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await axiosInstance.get("/api/reports/templates");
      setTemplates(safeArray(res?.data));
      setDebugState((prev) => ({
        ...prev,
        stage: "templates-loaded",
        message: `Loaded ${safeArray(res?.data).length} templates`,
      }));
    } catch (err) {
      console.error("Failed to load templates:", err);
      triggerAlert("Failed to load templates", "error");
      setTemplates([]);
      setDebugState((prev) => ({
        ...prev,
        stage: "templates-load-failed",
        message: err?.response?.data?.detail || err?.message || "Failed to load templates",
      }));
    } finally {
      setLoadingTemplates(false);
    }
  };

  const resetFormForNewTemplate = () => {
    setSelectedTemplateId("");
    setSelectedTemplate(null);
    setReportName("");
    setSourceView("");
    setIsViewBacked(false);
    setIsStandard(false);
    setAvailableToClients(false);
    setAvailableFields([]);
    setSelectedFields([]);
    setStartDate(null);
    setEndDate(null);
    setReportResult(null);
    setShowModal(false);
    setMobileFieldPanel("available");
    setDebugState({
      stage: "reset-new-template",
      message: "Form reset for new template",
      lastPayload: null,
      lastResponseSummary: null,
    });
  };

  const handleTemplateSelect = async (templateId) => {
    if (!templateId) {
      resetFormForNewTemplate();
      return;
    }

    setSelectedTemplateId(String(templateId));
    setDebugState((prev) => ({
      ...prev,
      stage: "template-loading",
      message: `Loading template ${templateId}`,
    }));

    try {
      const [tplRes, fieldsRes] = await Promise.all([
        axiosInstance.get(`/api/reports/templates/${templateId}`),
        axiosInstance.get(`/api/reports/templates/${templateId}/available-fields`),
      ]);

      const tpl = tplRes.data || {};
      const avail = fieldsRes.data || {};

      setSelectedTemplate(tpl);
      setReportName(tpl.name || "");
      setSelectedFields(safeArray(tpl.fields));
      setAvailableToClients(!!tpl.available_to_clients);
      setStartDate(tpl.start_date ? parseISO(tpl.start_date) : null);
      setEndDate(tpl.end_date ? parseISO(tpl.end_date) : null);
      setIsStandard(!!tpl.is_standard);

      setSourceView(avail.source_view || "");
      setIsViewBacked(!!avail.is_view_backed);
      setAvailableFields(safeArray(avail.fields));

      setDebugState((prev) => ({
        ...prev,
        stage: "template-loaded",
        message: `Loaded template "${tpl.name || templateId}" with ${safeArray(avail.fields).length} available fields`,
      }));
    } catch (err) {
      console.error("Failed to load template details:", err);
      triggerAlert("Failed to load template details", "error");
      setDebugState((prev) => ({
        ...prev,
        stage: "template-load-failed",
        message: err?.response?.data?.detail || err?.message || "Failed to load template details",
      }));
    }
  };

  const handleDeleteTemplate = async () => {
    if (!selectedTemplateId) return;

    if (!canDeleteExistingTemplate) {
      triggerAlert("Only admins can delete existing templates", "warning");
      return;
    }

    const confirmed = window.confirm("Delete this template permanently?");
    if (!confirmed) return;

    try {
      await axiosInstance.delete(`/api/reports/templates/${selectedTemplateId}`);
      triggerAlert("Template deleted", "success");
      resetFormForNewTemplate();
      await loadTemplates();
    } catch (err) {
      console.error("Delete failed:", err);
      triggerAlert(err?.response?.data?.detail || "Failed to delete template", "error");
      setDebugState((prev) => ({
        ...prev,
        stage: "delete-failed",
        message: err?.response?.data?.detail || err?.message || "Delete failed",
      }));
    }
  };

  const toggleField = (field) => {
    setSelectedFields((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]
    );
  };

  const moveSelectedFieldUp = (field) => {
    setSelectedFields((prev) => {
      const idx = prev.indexOf(field);
      if (idx <= 0) return prev;
      return moveItem(prev, idx, idx - 1);
    });
  };

  const moveSelectedFieldDown = (field) => {
    setSelectedFields((prev) => {
      const idx = prev.indexOf(field);
      if (idx < 0 || idx >= prev.length - 1) return prev;
      return moveItem(prev, idx, idx + 1);
    });
  };

  const moveSelectedFieldToTop = (field) => {
    setSelectedFields((prev) => {
      const idx = prev.indexOf(field);
      if (idx <= 0) return prev;
      return moveItem(prev, idx, 0);
    });
  };

  const moveSelectedFieldToBottom = (field) => {
    setSelectedFields((prev) => {
      const idx = prev.indexOf(field);
      if (idx < 0 || idx === prev.length - 1) return prev;
      return moveItem(prev, idx, prev.length - 1);
    });
  };

  const handlePreview = async () => {
    console.log("[DynamicReportBuilder] Preview clicked", {
      selectedTemplateId,
      reportName,
      selectedFields,
      startDate,
      endDate,
      availableToClients,
      sourceView,
      isViewBacked,
    });

    if (!reportName.trim()) {
      triggerAlert("Report name is required", "warning");
      setDebugState((prev) => ({
        ...prev,
        stage: "preview-blocked",
        message: "Preview blocked: report name missing",
      }));
      return;
    }

    if (!selectedFields.length) {
      triggerAlert("Select at least one field", "warning");
      setDebugState((prev) => ({
        ...prev,
        stage: "preview-blocked",
        message: "Preview blocked: no fields selected",
      }));
      return;
    }

    if (!startDate || !endDate) {
      triggerAlert("Select both start and end dates", "warning");
      setDebugState((prev) => ({
        ...prev,
        stage: "preview-blocked",
        message: "Preview blocked: start/end date missing",
      }));
      return;
    }

    const payload = {
      template_id: selectedTemplateId ? Number(selectedTemplateId) : null,
      name: reportName.trim(),
      fields: selectedFields,
      start_date: format(startDate, "yyyy-MM-dd"),
      end_date: format(endDate, "yyyy-MM-dd"),
      available_to_clients: availableToClients,
    };

    console.log("[DynamicReportBuilder] Preview payload", payload);

    setDebugState((prev) => ({
      ...prev,
      stage: "preview-posting",
      message: "Posting preview request...",
      lastPayload: payload,
    }));

    try {
      const res = await axiosInstance.post("/api/reports/run", payload);

      console.log("[DynamicReportBuilder] Preview response", {
        status: res?.status,
        data: res?.data,
      });

      const rowCount = Array.isArray(res?.data?.data) ? res.data.data.length : null;

      setReportResult(res.data);
      setShowModal(true);

      setDebugState((prev) => ({
        ...prev,
        stage: "preview-success",
        message: `Preview response received. Rows: ${rowCount ?? "unknown"}`,
        lastResponseSummary: {
          status: res?.status,
          hasDataArray: Array.isArray(res?.data?.data),
          rowCount,
          hasUid: !!res?.data?.timestamp_uid,
        },
      }));

      if (!Array.isArray(res?.data?.data)) {
        triggerAlert("Preview response shape is unexpected. Check browser console.", "warning");
      } else if (res.data.data.length === 0) {
        triggerAlert("Preview returned 0 rows", "warning");
      } else {
        triggerAlert(`Preview loaded: ${res.data.data.length} rows`, "success");
      }
    } catch (err) {
      console.error("[DynamicReportBuilder] Preview failed", err, err?.response?.data);
      triggerAlert(err?.response?.data?.detail || "Failed to run report", "error");
      setDebugState((prev) => ({
        ...prev,
        stage: "preview-failed",
        message: err?.response?.data?.detail || err?.message || "Preview failed",
        lastResponseSummary: {
          status: err?.response?.status || null,
          data: err?.response?.data || null,
        },
      }));
    }
  };

  const handleSaveTemplate = async () => {
    if (!reportName.trim()) {
      triggerAlert("Report name is required", "warning");
      return;
    }

    if (!selectedFields.length) {
      triggerAlert("Select at least one field", "warning");
      return;
    }

    if (!startDate || !endDate) {
      triggerAlert("Select both start and end dates", "warning");
      return;
    }

    const payload = {
      name: reportName.trim(),
      fields: selectedFields,
      start_date: format(startDate, "yyyy-MM-dd"),
      end_date: format(endDate, "yyyy-MM-dd"),
      available_to_clients: availableToClients,
      is_standard: canAdminManageTemplates ? !!isStandard : false,
    };

    try {
      if (selectedTemplateId && canUpdateExistingTemplate) {
        await axiosInstance.put(`/api/reports/templates/${selectedTemplateId}`, payload);
        triggerAlert("Template updated", "success");
      } else {
        await axiosInstance.post("/api/reports/create", payload);
        triggerAlert(mustSaveAsNew ? "Template saved as new" : "Template created", "success");
      }

      await loadTemplates();
    } catch (err) {
      console.error("Save template failed:", err);
      triggerAlert(err?.response?.data?.detail || "Failed to save template", "error");
      setDebugState((prev) => ({
        ...prev,
        stage: "save-failed",
        message: err?.response?.data?.detail || err?.message || "Save failed",
      }));
    }
  };

  const accent = theme?.accent_color || "#ff7a00";

  const buttonBase =
    "text-white text-xs font-semibold py-2 px-3 rounded" +
    (isMobile ? " min-h-[44px] w-full" : "");
  const generalBtnClass =
    "bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick " + buttonBase;
  const actionBtnClass =
    "bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick " + buttonBase;

  const orderPillStyle = {
    border: `1px solid ${accent}`,
    color: accent,
    background: "transparent",
    borderRadius: 999,
    padding: isMobile ? "9px 12px" : "3px 7px",
    fontSize: isMobile ? 13 : 10,
    fontWeight: 700,
    lineHeight: 1.1,
    minWidth: isMobile ? 44 : 0,
    minHeight: isMobile ? 44 : undefined,
  };

  const disabledOrderPillStyle = {
    ...orderPillStyle,
    opacity: 0.35,
    cursor: "not-allowed",
  };

  return (
    <>
      <div ref={rootRef} className="space-y-4 text-sm">
        <div className="border rounded p-3">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_auto] gap-3 items-end">
            <div className="min-w-0">
              <label className="block font-medium mb-1">Load Template</label>
              <select
                value={selectedTemplateId}
                onChange={(e) => handleTemplateSelect(e.target.value)}
                className={`w-full rounded border px-3 py-2 ${isMobile ? "min-h-[44px] text-base" : "text-sm"}`}
              >
                <option value="">Start a new template...</option>
                {templates.map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>
                    {tpl.name}
                    {tpl.is_standard ? " [Locked]" : ""}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={loadTemplates}
              className={generalBtnClass}
              disabled={loadingTemplates}
            >
              {loadingTemplates ? "Refreshing..." : "Refresh"}
            </button>

            <button
              type="button"
              onClick={handleDeleteTemplate}
              className={`flex items-center justify-center gap-1 bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-xs font-semibold px-3 py-2 rounded ${
                isMobile ? "min-h-[44px] w-full" : ""
              }`}
              disabled={!canDeleteExistingTemplate}
              title={canDeleteExistingTemplate ? "Delete selected template" : "Only admins can delete existing templates"}
            >
              <FaTrash size={14} />
              Delete Template
            </button>
          </div>

          {!!selectedTemplate && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-gray-600">
              <div>
                Source: <span className="font-mono">{sourceView || "template/model-backed"}</span>
              </div>
              <div>
                Mode:{" "}
                <span className="font-medium">
                  {isStandard ? "Admin locked template" : "Editable template"}
                </span>
              </div>
              <div>
                Permissions:{" "}
                <span className="font-medium">
                  {canAdminManageTemplates
                    ? "Admin can edit/delete existing templates"
                    : "Non-admin can run and save as new only"}
                </span>
              </div>
              <div>
                Available fields: <span className="font-medium">{availableFields.length}</span>
              </div>
              <div>
                Backing type:{" "}
                <span className="font-medium">
                  {isViewBacked ? "View-backed" : "Template/model-backed"}
                </span>
              </div>
            </div>
          )}
        </div>

        {isMobile ? (
          <details className="border rounded p-3 bg-gray-50">
            <summary className="text-sm font-semibold cursor-pointer min-h-[44px] flex items-center">
              Debug Status
            </summary>
            <div className="text-xs text-gray-700 space-y-1 pt-2 break-words">
              <div><strong>Stage:</strong> {debugState.stage}</div>
              <div><strong>Message:</strong> {debugState.message || "—"}</div>
              <div><strong>Modal flag:</strong> {showModal ? "true" : "false"}</div>
              <div><strong>Has result:</strong> {reportResult ? "true" : "false"}</div>
              <div><strong>Rows:</strong> {Array.isArray(reportResult?.data) ? reportResult.data.length : "—"}</div>
            </div>
          </details>
        ) : (
          <div className="border rounded p-3 bg-gray-50">
            <div className="text-sm font-semibold mb-2">Debug Status</div>
            <div className="text-xs text-gray-700 space-y-1">
              <div><strong>Stage:</strong> {debugState.stage}</div>
              <div><strong>Message:</strong> {debugState.message || "—"}</div>
              <div><strong>Modal flag:</strong> {showModal ? "true" : "false"}</div>
              <div><strong>Has result:</strong> {reportResult ? "true" : "false"}</div>
              <div><strong>Rows:</strong> {Array.isArray(reportResult?.data) ? reportResult.data.length : "—"}</div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="border rounded p-3">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="min-w-0">
                <label className="block font-medium mb-1">Report Name</label>
                <input
                  type="text"
                  className={`w-full rounded border px-3 py-2 ${isMobile ? "min-h-[44px] text-base" : "text-sm"}`}
                  value={reportName}
                  onChange={(e) => setReportName(e.target.value)}
                />
              </div>

              <div className={`min-w-0 flex items-center gap-2 ${isMobile ? "pt-2 min-h-[44px]" : "pt-6"}`}>
                <input
                  id="clientAvailable"
                  type="checkbox"
                  className={isMobile ? "mr-2 w-5 h-5 shrink-0" : "mr-2"}
                  checked={availableToClients}
                  onChange={(e) => setAvailableToClients(e.target.checked)}
                  style={{
                    accentColor: theme?.secondary_color || "#e67e22",
                  }}
                />
                <label htmlFor="clientAvailable">
                  Make this report available in the Client Portal
                </label>
              </div>
            </div>

            {canAdminManageTemplates && (
              <div className={`mt-3 flex items-center gap-2 ${isMobile ? "min-h-[44px]" : ""}`}>
                <input
                  id="isStandard"
                  type="checkbox"
                  className={isMobile ? "w-5 h-5 shrink-0" : ""}
                  checked={isStandard}
                  onChange={(e) => setIsStandard(e.target.checked)}
                  style={{
                    accentColor: theme?.secondary_color || "#e67e22",
                  }}
                />
                <label htmlFor="isStandard">
                  Admin lock this template (stored as standard / locked)
                </label>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="border rounded p-3">
              <label className="block font-medium mb-1">Start Date</label>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                placeholderText="Select start date"
                className={`w-full rounded border px-3 py-2 ${isMobile ? "min-h-[44px] text-base" : "text-sm"}`}
                wrapperClassName="w-full"
                calendarClassName="react-datepicker-theme"
                popperClassName="z-[9999]"
                popperPlacement="bottom-start"
                portalId="root-portal"
              />
            </div>

            <div className="border rounded p-3">
              <label className="block font-medium mb-1">End Date</label>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                placeholderText="Select end date"
                className={`w-full rounded border px-3 py-2 ${isMobile ? "min-h-[44px] text-base" : "text-sm"}`}
                wrapperClassName="w-full"
                calendarClassName="react-datepicker-theme"
                popperClassName="z-[9999]"
                popperPlacement="bottom-start"
                portalId="root-portal"
              />
            </div>
          </div>

          {isMobile && (
            <div
              className="grid grid-cols-2 gap-2"
              role="tablist"
              aria-label="Report field workspace"
            >
              <button
                type="button"
                onClick={() => setMobileFieldPanel("available")}
                className="min-h-[44px] rounded border px-3 py-2 text-sm font-semibold"
                style={{
                  borderColor: mobileFieldPanel === "available" ? accent : "#d1d5db",
                  color: mobileFieldPanel === "available" ? accent : "#4b5563",
                  background:
                    mobileFieldPanel === "available"
                      ? "rgba(0,0,0,0.02)"
                      : "#fff",
                }}
                aria-selected={mobileFieldPanel === "available"}
              >
                Available ({availableFields.length})
              </button>

              <button
                type="button"
                onClick={() => setMobileFieldPanel("selected")}
                className="min-h-[44px] rounded border px-3 py-2 text-sm font-semibold"
                style={{
                  borderColor: mobileFieldPanel === "selected" ? accent : "#d1d5db",
                  color: mobileFieldPanel === "selected" ? accent : "#4b5563",
                  background:
                    mobileFieldPanel === "selected"
                      ? "rgba(0,0,0,0.02)"
                      : "#fff",
                }}
                aria-selected={mobileFieldPanel === "selected"}
              >
                Selected ({selectedFields.length})
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {(!isMobile || mobileFieldPanel === "available") && (
              <div
                className={`border rounded p-3 flex flex-col overflow-hidden ${
                  isMobile ? "min-h-[360px] max-h-[520px]" : "min-h-[420px]"
                }`}
              >
              <div className="text-sm font-semibold text-gray-800 mb-2 shrink-0">
                Available Fields
              </div>
              <div className="text-xs text-gray-500 mb-3 shrink-0">
                Loaded from the actual report source/view where available.
              </div>

              <div className="flex flex-wrap gap-2 mb-3 shrink-0">
                <button
                  type="button"
                  onClick={() => setSelectedFields(availableFields)}
                  className={generalBtnClass}
                >
                  Select All
                </button>

                <button
                  type="button"
                  onClick={() => setSelectedFields([])}
                  className={generalBtnClass}
                >
                  Clear All
                </button>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pr-1">
                {!availableFields.length ? (
                  <div className="text-sm text-gray-500">
                    Load a template to retrieve available fields from its source.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-2">
                    {availableFields.map((field) => {
                      const checked = selectedFieldsSet.has(field);
                      return (
                        <label
                          key={field}
                          className="flex items-start gap-2 text-sm min-w-0"
                          title={`${FRIENDLY_FIELD_NAMES[field] || field} (${field})`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleField(field)}
                            className={isMobile ? "mt-[2px] shrink-0 w-5 h-5" : "mt-[2px] shrink-0"}
                          />
                          <span className="break-words leading-5 min-w-0">
                            <span className="font-medium">{FRIENDLY_FIELD_NAMES[field] || field}</span>
                            {FRIENDLY_FIELD_NAMES[field] && (
                              <span className="text-gray-500"> — {field}</span>
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              </div>
            )}

            {(!isMobile || mobileFieldPanel === "selected") && (
              <div
                className={`border rounded p-3 flex flex-col overflow-hidden ${
                  isMobile ? "min-h-[360px] max-h-[520px]" : "min-h-[420px]"
                }`}
              >
              <div className="text-sm font-semibold text-gray-800 mb-2 shrink-0">
                Selected Fields Order
              </div>
              <div className="text-xs text-gray-500 mb-3 shrink-0">
                This order is used for preview and export.
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pr-1">
                {!selectedFields.length ? (
                  <div className="text-sm text-gray-500">No fields selected.</div>
                ) : (
                  <div className="space-y-2">
                    {selectedFields.map((field, index) => {
                      const isFirst = index === 0;
                      const isLast = index === selectedFields.length - 1;

                      return (
                        <div
                          key={`selected-${field}`}
                          className={`border rounded flex items-start gap-2 min-w-0 ${isMobile ? "p-3" : "p-2"}`}
                        >
                          <div
                            className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                            style={{
                              background: accent,
                              color: "#fff",
                            }}
                            title={`Position ${index + 1}`}
                          >
                            {index + 1}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium break-words">
                              {FRIENDLY_FIELD_NAMES[field] || field}
                            </div>
                            <div className="text-xs text-gray-500 break-words">{field}</div>

                            <div className="flex flex-wrap gap-1 mt-2">
                              <button
                                type="button"
                                onClick={() => moveSelectedFieldToTop(field)}
                                disabled={isFirst}
                                style={isFirst ? disabledOrderPillStyle : orderPillStyle}
                              >
                                Top
                              </button>

                              <button
                                type="button"
                                onClick={() => moveSelectedFieldUp(field)}
                                disabled={isFirst}
                                style={isFirst ? disabledOrderPillStyle : orderPillStyle}
                              >
                                ↑
                              </button>

                              <button
                                type="button"
                                onClick={() => moveSelectedFieldDown(field)}
                                disabled={isLast}
                                style={isLast ? disabledOrderPillStyle : orderPillStyle}
                              >
                                ↓
                              </button>

                              <button
                                type="button"
                                onClick={() => moveSelectedFieldToBottom(field)}
                                disabled={isLast}
                                style={isLast ? disabledOrderPillStyle : orderPillStyle}
                              >
                                Bottom
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {!!unselectedFields.length && (
                <div className="pt-2 mt-2 border-t shrink-0 min-w-0">
                  <div className="text-xs text-gray-500 break-words">
                    Unselected fields:{" "}
                    {unselectedFields
                      .map((field) => FRIENDLY_FIELD_NAMES[field] || field)
                      .join(", ")}
                  </div>
                </div>
              )}
              </div>
            )}
          </div>

          <div className={`flex flex-wrap gap-2 pt-2 ${isMobile ? "flex-col" : ""}`}>
            <button
              type="button"
              onClick={handlePreview}
              className={actionBtnClass}
            >
              Preview Report
            </button>

            <button
              type="button"
              onClick={handleSaveTemplate}
              className={generalBtnClass}
            >
              {selectedTemplateId && canUpdateExistingTemplate
                ? "Update Existing Template"
                : mustSaveAsNew
                ? "Save As New Template"
                : "Save Template"}
            </button>

            {selectedTemplateId && !canAdminManageTemplates && (
              <div className={`text-xs text-gray-600 flex items-center ${isMobile ? "w-full py-1" : ""}`}>
                Existing templates are locked for non-admin users. Save As New will create your own copy.
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && reportResult && (
        <ReportDisplayModal
          onClose={() => setShowModal(false)}
          data={reportResult.data}
          fields={selectedFields}
          uid={reportResult.timestamp_uid}
          meta={{
            name: reportName,
            start_date: startDate ? format(startDate, "yyyy-MM-dd") : null,
            end_date: endDate ? format(endDate, "yyyy-MM-dd") : null,
            available_to_clients: availableToClients,
          }}
        />
      )}
    </>
  );
};

export default DynamicReportBuilder;